'use es6';

import { isEqual } from 'underscore';
/*
 * State
 */

/*
{
  byId: {
    12345: {
      selectedWidgets: {
        order: [widgetId, widgetId],
        widgets: {
          [widgetId]: { widgetId },
          [widgetId]: { widgetId },
        },
      },
      widgetContentEditor: {
        widgetId: 'module_1234',
        selectedField: null,
      },
      smartContentEditor: {
        widgetId: 'module_1234',
        selectedRuleId: null,
      },
    },
  },
}
*/

import { LAYOUT_EDITOR_SESSION_SET_WIDGET_SELECTIONS, LAYOUT_EDITOR_SESSION_CLEAR_WIDGET_SELECTIONS, LAYOUT_EDITOR_SESSION_ADD_WIDGET_SELECTION, LAYOUT_EDITOR_SESSION_REMOVE_WIDGET_SELECTION, LAYOUT_EDITOR_SESSION_TOGGLE_WIDGET_SELECTION, LAYOUT_EDITOR_SESSION_WIDGET_CONTENT_EDITOR_OPEN, LAYOUT_EDITOR_SESSION_WIDGET_CONTENT_EDITOR_CLOSE, LAYOUT_EDITOR_SESSION_FULL_SCREEN_CODE_EDITOR_OPEN, LAYOUT_EDITOR_SESSION_FULL_SCREEN_CODE_EDITOR_CLOSE, LAYOUT_EDITOR_SESSION_WIDGET_SELECT_SMART_RULE, LAYOUT_WIDGET_REMOVE_SMART_RULE, LAYOUT_WIDGET_ADD_SMART_RULE, LAYOUT_WIDGET_UPDATE_SMART_RULES, LAYOUT_REVERT, LAYOUT_DELETE, LAYOUT_WIDGET_DELETE, LAYOUT_WIDGET_SWAP, LAYOUT_EDITOR_SESSION_REVISION_ACTIVATE, LAYOUT_EDITOR_SESSION_REVISION_DEACTIVATE, LAYOUT_EDITOR_SESSION_BLOG_CONTENT_MODULE_EDITOR_OPEN, LAYOUT_EDITOR_SESSION_BLOG_CONTENT_MODULE_EDITOR_CLOSE, LAYOUT_EDITOR_SESSION_HEAD_BODY_EDITOR_OPEN, LAYOUT_EDITOR_SESSION_HEAD_BODY_EDITOR_CLOSE, LAYOUT_EDITOR_SESSION_SET_EDITING_LANGUAGE, LAYOUT_LOCALIZATION_CHANGE_MASTER_LANGUAGE, LAYOUT_LOCALIZATION_DELETE_LANGUAGE, LAYOUT_EDITOR_SESSION_SET_EDITOR_MODE } from 'DesignData/actions/ActionTypes';
import SeamlessImmutable from 'seamless-immutable';
import { combineReducers } from 'redux';
import { POST } from 'DesignData/enums/BlogContentModuleTemplates';
import { DEFAULT } from 'DesignData/enums/EditorModes';
const EMPTY_SELECTED_WIDGETS = SeamlessImmutable({
  // Order selected
  order: [],
  widgets: {}
});

const removeSelectedWidget = (state, widgetId) => {
  if (!state.widgets[widgetId]) {
    return state;
  }

  const removeIndex = state.order.indexOf(widgetId);
  const order = [...state.order.slice(0, removeIndex), ...state.order.slice(removeIndex + 1)];
  const widgets = state.widgets.without(widgetId);
  return state.merge({
    order,
    widgets
  });
};

const addSelectedWidget = (state, widgetId) => {
  const __state = removeSelectedWidget(state, widgetId);

  const order = __state.order.concat(widgetId);

  const widgets = __state.widgets.set(widgetId, {
    widgetId
  });

  return __state.merge({
    order,
    widgets
  });
};
/**
 * The currently selected widget details.
 */


function selectedWidgets(state = EMPTY_SELECTED_WIDGETS, action) {
  switch (action.type) {
    case LAYOUT_REVERT:
    case LAYOUT_EDITOR_SESSION_CLEAR_WIDGET_SELECTIONS:
      {
        return EMPTY_SELECTED_WIDGETS;
      }

    case LAYOUT_EDITOR_SESSION_TOGGLE_WIDGET_SELECTION:
      {
        const {
          widgetId,
          isMultiSelect
        } = action; // Toggle OFF

        if (state.widgets[widgetId]) {
          if (!isMultiSelect) {
            return EMPTY_SELECTED_WIDGETS;
          }

          return removeSelectedWidget(state, widgetId);
        } // Toggle ON


        if (!isMultiSelect) {
          return addSelectedWidget(EMPTY_SELECTED_WIDGETS, widgetId);
        }

        return addSelectedWidget(state, widgetId);
      }

    case LAYOUT_EDITOR_SESSION_SET_WIDGET_SELECTIONS:
      {
        const {
          widgetIds
        } = action;

        if (!widgetIds.length) {
          return EMPTY_SELECTED_WIDGETS;
        }

        const newState = widgetIds.reduce(addSelectedWidget, EMPTY_SELECTED_WIDGETS);
        return isEqual(state, newState) ? state : newState;
      }

    case LAYOUT_EDITOR_SESSION_ADD_WIDGET_SELECTION:
      {
        const {
          widgetId,
          isMultiSelect
        } = action;

        const __state = isMultiSelect ? state : EMPTY_SELECTED_WIDGETS;

        return addSelectedWidget(__state, widgetId);
      }

    case LAYOUT_EDITOR_SESSION_REMOVE_WIDGET_SELECTION:
      {
        const {
          widgetId,
          isMultiSelect
        } = action;

        if (!isMultiSelect) {
          return EMPTY_SELECTED_WIDGETS;
        }

        return removeSelectedWidget(state, widgetId);
      }

    case LAYOUT_WIDGET_DELETE:
      {
        const {
          widgets
        } = action;
        return widgets.reduce((acc, widget) => {
          const {
            widgetId
          } = widget;
          return removeSelectedWidget(acc, widgetId);
        }, state);
      }

    default:
      return state;
  }
}

const EMPTY_ACTIVATED_WIDGET_CONTENT_EDITOR = SeamlessImmutable({
  widgetId: null,
  //  To distinguish if multiple fields are available - `blog_content` has two editable code fields.
  selectedField: null
});
/**
 * Session data for active WidgetContentEditor
 */

function widgetContentEditor(state = EMPTY_ACTIVATED_WIDGET_CONTENT_EDITOR, action) {
  switch (action.type) {
    case LAYOUT_EDITOR_SESSION_WIDGET_CONTENT_EDITOR_OPEN:
      {
        const {
          widgetId
        } = action;
        const selectedField = action.selectedField || state.selectedField || EMPTY_ACTIVATED_WIDGET_CONTENT_EDITOR.selectedField;
        return state.merge({
          widgetId,
          selectedField
        });
      }

    case LAYOUT_REVERT:
    case LAYOUT_EDITOR_SESSION_WIDGET_CONTENT_EDITOR_CLOSE:
    case LAYOUT_EDITOR_SESSION_BLOG_CONTENT_MODULE_EDITOR_OPEN:
    case LAYOUT_EDITOR_SESSION_FULL_SCREEN_CODE_EDITOR_OPEN:
    case LAYOUT_EDITOR_SESSION_HEAD_BODY_EDITOR_OPEN:
      {
        return EMPTY_ACTIVATED_WIDGET_CONTENT_EDITOR;
      }

    case LAYOUT_WIDGET_SWAP:
      {
        return action.widgetId === state.widgetId ? EMPTY_ACTIVATED_WIDGET_CONTENT_EDITOR : state;
      }

    case LAYOUT_WIDGET_DELETE:
      {
        const isActiveDeleted = action.widgets.some(({
          widgetId
        }) => widgetId === state.widgetId);
        return isActiveDeleted ? EMPTY_ACTIVATED_WIDGET_CONTENT_EDITOR : state;
      }

    default:
      return state;
  }
}

const EMPTY_BLOG_CONTENT_TEMPLATES_EDITOR = SeamlessImmutable({
  widgetId: null,
  selectedTemplate: POST
});
/**
 * Session data for active blog content templates editor
 */

function blogContentTemplatesEditor(state = EMPTY_ACTIVATED_WIDGET_CONTENT_EDITOR, action) {
  switch (action.type) {
    case LAYOUT_EDITOR_SESSION_BLOG_CONTENT_MODULE_EDITOR_OPEN:
      {
        const {
          widgetId,
          selectedTemplate
        } = action;
        return state.merge({
          widgetId,
          selectedTemplate
        });
      }

    case LAYOUT_REVERT:
    case LAYOUT_EDITOR_SESSION_BLOG_CONTENT_MODULE_EDITOR_CLOSE:
    case LAYOUT_EDITOR_SESSION_WIDGET_CONTENT_EDITOR_OPEN:
    case LAYOUT_EDITOR_SESSION_FULL_SCREEN_CODE_EDITOR_OPEN:
    case LAYOUT_EDITOR_SESSION_HEAD_BODY_EDITOR_OPEN:
      {
        return EMPTY_BLOG_CONTENT_TEMPLATES_EDITOR;
      }

    case LAYOUT_WIDGET_SWAP:
      {
        return action.widgetId === state.widgetId ? EMPTY_BLOG_CONTENT_TEMPLATES_EDITOR : state;
      }

    case LAYOUT_WIDGET_DELETE:
      {
        const isActiveDeleted = action.widgets.some(({
          widgetId
        }) => widgetId === state.widgetId);
        return isActiveDeleted ? EMPTY_BLOG_CONTENT_TEMPLATES_EDITOR : state;
      }

    default:
      return state;
  }
}

const EMPTY_CUSTOM_HTML_EDITOR = SeamlessImmutable({
  widgetId: null,
  fieldProps: null
});
/**
 * Sessions data  for active custom html editor
 */

function fullScreenCodeEditor(state = EMPTY_CUSTOM_HTML_EDITOR, action) {
  switch (action.type) {
    case LAYOUT_EDITOR_SESSION_FULL_SCREEN_CODE_EDITOR_OPEN:
      {
        const {
          widgetId,
          fieldProps
        } = action;
        return state.merge({
          widgetId,
          fieldProps
        });
      }

    case LAYOUT_REVERT:
    case LAYOUT_EDITOR_SESSION_FULL_SCREEN_CODE_EDITOR_CLOSE:
    case LAYOUT_EDITOR_SESSION_WIDGET_CONTENT_EDITOR_OPEN:
    case LAYOUT_EDITOR_SESSION_BLOG_CONTENT_MODULE_EDITOR_OPEN:
    case LAYOUT_EDITOR_SESSION_HEAD_BODY_EDITOR_OPEN:
      {
        return EMPTY_CUSTOM_HTML_EDITOR;
      }

    case LAYOUT_WIDGET_SWAP:
      {
        return action.widgetId === state.widgetId ? EMPTY_CUSTOM_HTML_EDITOR : state;
      }

    case LAYOUT_WIDGET_DELETE:
      {
        const isActiveDeleted = action.widgets.some(({
          widgetId
        }) => widgetId === state.widgetId);
        return isActiveDeleted ? EMPTY_BLOG_CONTENT_TEMPLATES_EDITOR : state;
      }

    default:
      return state;
  }
}

const EMPTY_HEAD_BODY_EDITOR = {
  selectedField: null
};
/**
 * Session data for head/body options
 */

function fullScreenHeadBodyEditor(state = EMPTY_HEAD_BODY_EDITOR, action) {
  switch (action.type) {
    case LAYOUT_EDITOR_SESSION_HEAD_BODY_EDITOR_OPEN:
      {
        const {
          selectedField
        } = action;
        return state.merge({
          selectedField
        });
      }

    case LAYOUT_REVERT:
    case LAYOUT_EDITOR_SESSION_HEAD_BODY_EDITOR_CLOSE:
    case LAYOUT_EDITOR_SESSION_WIDGET_CONTENT_EDITOR_OPEN:
    case LAYOUT_EDITOR_SESSION_BLOG_CONTENT_MODULE_EDITOR_OPEN:
    case LAYOUT_EDITOR_SESSION_FULL_SCREEN_CODE_EDITOR_OPEN:
      {
        return EMPTY_HEAD_BODY_EDITOR;
      }

    default:
      return state;
  }
}

const EMPTY_SMART_CONTENT_EDITOR = SeamlessImmutable({
  widgetId: null,
  selectedRuleId: null
});
/**
 * Session data for smart content editor.
 */

function smartContentEditor(state = EMPTY_SMART_CONTENT_EDITOR, action) {
  switch (action.type) {
    case LAYOUT_EDITOR_SESSION_WIDGET_SELECT_SMART_RULE:
      {
        const {
          widgetId,
          selectedRuleId
        } = action;
        return selectedRuleId ? state.merge({
          widgetId,
          selectedRuleId
        }) : EMPTY_SMART_CONTENT_EDITOR;
      }

    case LAYOUT_WIDGET_REMOVE_SMART_RULE:
      {
        const {
          smartRuleId
        } = action;
        return state.selectedRuleId === smartRuleId ? EMPTY_SMART_CONTENT_EDITOR : state;
      }

    case LAYOUT_WIDGET_ADD_SMART_RULE:
      {
        const {
          smartRuleId
        } = action;
        return state.merge({
          selectedRuleId: smartRuleId
        });
      }

    case LAYOUT_WIDGET_UPDATE_SMART_RULES:
      {
        return state.merge({
          selectedRuleId: null
        });
      }

    case LAYOUT_REVERT:
    case LAYOUT_EDITOR_SESSION_ADD_WIDGET_SELECTION:
    case LAYOUT_EDITOR_SESSION_REMOVE_WIDGET_SELECTION:
    case LAYOUT_EDITOR_SESSION_TOGGLE_WIDGET_SELECTION:
    case LAYOUT_WIDGET_SWAP:
      {
        return EMPTY_SMART_CONTENT_EDITOR;
      }

    case LAYOUT_WIDGET_DELETE:
      {
        const isActiveDeleted = action.widgets.some(({
          widgetId
        }) => widgetId === state.widgetId);
        return isActiveDeleted ? EMPTY_SMART_CONTENT_EDITOR : state;
      }

    default:
      return state;
  }
}
/**
 * High level determinations as to if active layout is a revision.
 */


function revisionMode(state = SeamlessImmutable({
  revisionId: undefined
}), action) {
  switch (action.type) {
    case LAYOUT_EDITOR_SESSION_REVISION_ACTIVATE:
    case LAYOUT_EDITOR_SESSION_REVISION_DEACTIVATE:
      return state.merge({
        revisionId: action.revisionId || undefined
      });

    default:
      return state;
  }
}
/**
 * Current language the user is editing you
 */


function editingLanguage(state = null, action) {
  switch (action.type) {
    case LAYOUT_EDITOR_SESSION_SET_EDITING_LANGUAGE:
      {
        return action.langCode;
      }

    case LAYOUT_LOCALIZATION_CHANGE_MASTER_LANGUAGE:
    case LAYOUT_LOCALIZATION_DELETE_LANGUAGE:
      {
        if (state === action.langCode) return null;
        return state;
      }

    default:
      return state;
  }
}
/**
 * Current editor mode
 */


function editorMode(state = DEFAULT, action) {
  switch (action.type) {
    case LAYOUT_EDITOR_SESSION_SET_EDITOR_MODE:
      {
        const {
          mode
        } = action;
        if (mode) return mode;
        return state;
      }

    default:
      return state;
  }
}
/*
 * Reducers nested under byId
 */


const byIdReducers = combineReducers({
  selectedWidgets,
  widgetContentEditor,
  smartContentEditor,
  revisionMode,
  blogContentTemplatesEditor,
  fullScreenCodeEditor,
  fullScreenHeadBodyEditor,
  editingLanguage,
  editorMode
});

function byId(state = SeamlessImmutable({}), action) {
  switch (action.type) {
    case LAYOUT_DELETE:
      {
        const {
          layoutId,
          deleted
        } = action;
        return deleted ? state.without(layoutId) : state;
      }

    default:
      {
        const {
          layoutId
        } = action;

        if (layoutId) {
          return state.set(layoutId, byIdReducers(state[layoutId], action));
        }

        if (state.default) {
          return state;
        }

        return state.set('default', byIdReducers(state.default, action));
      }
  }
}

export default combineReducers({
  byId
});